import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import { OutlierTimeOption, ExceptionStatus } from '@amzn/aet-dynamo-kit';

export const OUTLIER_DEFINITIONS = {
  RUDENESS: {
    value: 'Rudeness',
    i18nKey: 'cs_perfportal_category_rudeness',
    isZeroTolerance: true,
  },
  BACKGROUND_NOISE: {
    value: 'Background noise',
    i18nKey: 'cs_perfportal_category_background_noise',
    isZeroTolerance: false,
  },
  SILENCE_RATE: {
    value: 'Silence rate',
    i18nKey: 'cs_perfportal_category_silence_rate',
    isZeroTolerance: false,
  },
  EXCESSIVE_HOLD_TIMES: {
    value: 'Excessive hold times',
    i18nKey: 'cs_perfportal_category_excessive_hold_time',
    isZeroTolerance: true,
  },
  ASSOCIATE_DISCONNECTS: {
    value: 'Associate disconnects',
    i18nKey: 'cs_perfportal_category_associate_disconnects',
    isZeroTolerance: true,
  },
  INVALID_TRANSFERS: {
    value: 'Invalid transfers',
    i18nKey: 'cs_perfportal_category_invalid_transfers',
    isZeroTolerance: false,
  },
  INVALID_PARK: {
    value: 'Invalid park',
    i18nKey: 'cs_perfportal_category_invalid_park',
    isZeroTolerance: false,
  },
  EXCESSIVE_HOLD_NUM: {
    value: 'Excessive number of holds',
    i18nKey: 'cs_perfportal_category_excessive_hold_num',
    isZeroTolerance: true,
  },
  HOLD_TIME_RATE: {
    value: 'Hold time rate',
    i18nKey: 'cs_perfportal_category_hold_time_rate',
    isZeroTolerance: false,
  },
  CCX_BOTTOM_25: {
    value: 'Bottom 25% for CCX-Overall service Bottom-Box',
    i18nKey: 'cs_perfportal_category_ccx_bottom_box',
    isZeroTolerance: false,
  },
  DART_USAGE: {
    value: 'Escalation rate/DART usage',
    i18nKey: 'cs_perfportal_category_escalation_rate',
    isZeroTolerance: false,
  },
  MISSED_FOLLOWUP: {
    value: 'Missed follow-ups',
    i18nKey: 'cs_perfportal_category_missed_followups',
    isZeroTolerance: true,
  },
  MISSED_CONTACT: {
    value: 'Missed contact',
    i18nKey: 'cs_perfportal_category_missed_contact',
    isZeroTolerance: false,
  },
  DART_CONSULT_EFFORT: {
    value: 'DART consult effort',
    i18nKey: 'cs_perfportal_category_dart_consult',
    isZeroTolerance: false,
  },
  INVALID_CONCESSIONS: {
    value: 'Invalid concessions (out of policy)',
    i18nKey: 'cs_perfportal_category_invalid_concessions',
    isZeroTolerance: false,
  },
  MISSED_CONCESSIONS: {
    value: 'Missed concessions',
    i18nKey: 'cs_perfportal_category_missed_concessions',
    isZeroTolerance: false,
  },
  ACHT_BOTTOM_10: {
    value: 'ACHT (Bottom 10%)',
    i18nKey: 'cs_perfportal_category_acht_bottom',
    isZeroTolerance: false,
  },
  TIME_TO_CLOSE: {
    value: 'Time to close',
    i18nKey: 'cs_perfportal_category_time_to_close',
    isZeroTolerance: false,
  },
  SPAM_RATE: {
    value: 'Spam rate',
    i18nKey: 'cs_perfportal_category_spam_rate',
    isZeroTolerance: false,
  },
  DUPLICATE_RATE: {
    value: 'Duplicate rate',
    i18nKey: 'cs_perfportal_category_duplicate_rate',
    isZeroTolerance: false,
  },
  FALSE_PROMISE: {
    value: 'False promise',
    i18nKey: 'cs_perfportal_category_false_promise',
    isZeroTolerance: false,
  },
} as const;

export interface User {
  id: string;
  login: string;
}

export const UniversalFilterValue = {
  ALL: 'ALL',
} as const;

export type OutlierType =
  (typeof OUTLIER_DEFINITIONS)[keyof typeof OUTLIER_DEFINITIONS]['value'];

// CCP Link supported categories
export type CCPLinkSupportedCategory =
  | typeof OUTLIER_DEFINITIONS.ASSOCIATE_DISCONNECTS.value
  | typeof OUTLIER_DEFINITIONS.MISSED_FOLLOWUP.value;

// CCP Link LATAM supported categories (same as regular CCP Link for now)
export type CCPLinkLatamSupportedCategory =
  | typeof OUTLIER_DEFINITIONS.ASSOCIATE_DISCONNECTS.value
  | typeof OUTLIER_DEFINITIONS.MISSED_FOLLOWUP.value;

export type FilterStatusType =
  | ContactStatusOptions
  | typeof UniversalFilterValue.ALL;

export const FilterKey = {
  TIME_OPTIONS: 'timeOptions',
  TIME_OPTION: 'timeOption',
  VIEW_DATA_BY: 'viewDataBy',
  CATEGORY: 'category',
  ASSOCIATE: 'associate',
  TM: 'tm',
  STATUS: 'status',
} as const;

export type FilterKeyType = (typeof FilterKey)[keyof typeof FilterKey];

export interface FilterValues {
  timeOptions: string[];
  timeOption: string;
  viewDataBy: OutlierTimeOption;
  category: OutlierType | typeof UniversalFilterValue.ALL;
  associate: string | typeof UniversalFilterValue.ALL;
  tm: string | typeof UniversalFilterValue.ALL;
  status: FilterStatusType;
}

export interface OutlierContact {
  contactId: string;
  status: ContactStatusOptions;
  repeatedIssue: boolean;
  week: string;
  user: User;
  tmAlias?: string;
  category: OutlierType;
  justification?: string | null;
  exception?: {
    status: ExceptionStatus | null;
  };
}

export interface OutlierCategory {
  title: OutlierType;
  contacts: OutlierContact[];
}

export interface OutlierMetric {
  type: OutlierType;
  count: number;
  requiredReviewsRemaining?: number;
}

export interface ReviewMetric {
  count: number;
  percentage: number;
}

export interface RequiredReviewsData {
  totalRequired: number;
  remainingRequired: number;
  completedRequired: number;
}

export interface AssociateRequiredReviews {
  login: string;
  id: string;
  requiredReviews: RequiredReviewsData;
  categories: Record<string, RequiredReviewsData>;
}

export interface OutlierMetricsData {
  pendingOutliers: number;
  reviewedCount: number;
  totalCount: number;
  pendingOutlierTypes: OutlierMetric[];
  reviewMetrics: ReviewMetric;
  requiredReviews: RequiredReviewsData;
}

export interface AssociatesMetricsData {
  totalAssociates: number;
  pendingAssociateTypes: OutlierMetric[];
  associatesWithRequiredReviews: AssociateRequiredReviews[];
}

export interface DisplayMessage {
  id: string;
  defaultMessage: string;
  values?: Record<string, any>;
}

export interface FilterDisplay {
  prefix: DisplayMessage;
  value: DisplayMessage;
}

export type FilterValue =
  | string
  | OutlierType
  | ContactStatusOptions
  | typeof UniversalFilterValue.ALL;

export type FilterValueType = {
  [FilterKey.CATEGORY]: OutlierType | typeof UniversalFilterValue.ALL;
  [FilterKey.STATUS]: ContactStatusOptions | typeof UniversalFilterValue.ALL;
  [FilterKey.ASSOCIATE]: string | typeof UniversalFilterValue.ALL;
  [FilterKey.TM]: string | typeof UniversalFilterValue.ALL;
  [FilterKey.VIEW_DATA_BY]: OutlierTimeOption;
  [FilterKey.TIME_OPTION]: string;
  [FilterKey.TIME_OPTIONS]: string[];
};

export type AssociateDetails = {
  login: string;
  id: string;
};

export const BACKEND_CATEGORY_MAP = {
  rudeness: OUTLIER_DEFINITIONS.RUDENESS,
  background_noise: OUTLIER_DEFINITIONS.BACKGROUND_NOISE,
  high_silence: OUTLIER_DEFINITIONS.SILENCE_RATE,
  excessive_hold_time: OUTLIER_DEFINITIONS.EXCESSIVE_HOLD_TIMES,
  invalid_agent_disconnect: OUTLIER_DEFINITIONS.ASSOCIATE_DISCONNECTS,
  invalid_transfer: OUTLIER_DEFINITIONS.INVALID_TRANSFERS,
  invalid_park: OUTLIER_DEFINITIONS.INVALID_PARK,
  excessive_hold_num: OUTLIER_DEFINITIONS.EXCESSIVE_HOLD_NUM,
  hold_time_rate: OUTLIER_DEFINITIONS.HOLD_TIME_RATE,
  ccx_bottom_twobox_twentyfive: OUTLIER_DEFINITIONS.CCX_BOTTOM_25,
  dart_usage: OUTLIER_DEFINITIONS.DART_USAGE,
  missed_followup: OUTLIER_DEFINITIONS.MISSED_FOLLOWUP,
  missed_contact: OUTLIER_DEFINITIONS.MISSED_CONTACT,
  dart_consult_effort: OUTLIER_DEFINITIONS.DART_CONSULT_EFFORT,
  invalid_concessions: OUTLIER_DEFINITIONS.INVALID_CONCESSIONS,
  missed_concessions: OUTLIER_DEFINITIONS.MISSED_CONCESSIONS,
  acht_bottom_ten: OUTLIER_DEFINITIONS.ACHT_BOTTOM_10,
  time_to_close: OUTLIER_DEFINITIONS.TIME_TO_CLOSE,
  spam_rate: OUTLIER_DEFINITIONS.SPAM_RATE,
  duplicate_rate: OUTLIER_DEFINITIONS.DUPLICATE_RATE,
  false_promise: OUTLIER_DEFINITIONS.FALSE_PROMISE,
} as const;

export interface OutlierException {
  exceptionId: string;
  contactId: string;
  csaUserIdWithPrefix: string;
  omUserIdWithPrefix: string;
  tmAlias: string;
  csaAlias: string;
  category: string;
  outlierCalYear: string;
  status: ExceptionStatus | null;
  justification: string | null;
}

export interface OMOutlierSummaryStats {
  tmAlias: string;
  numAssociates: number;
  totalOutliers: number;
  numInvalidFlags: number;
  pendingOutliers: number;
  hasRepeatOutliers: boolean;
  requiredRemaining: number;
  totalRequired: number;
  tmId?: string;
  tmDisplayName?: string;
}

export interface OMPendingOutliersTMInfo {
  name: string;
  login: string;
  numAssociates: number;
  pendingOutliers: number;
  totalOutliers: number;
}

export interface OMExceptionMetricsWithDetails {
  exceptions: OutlierException[];
  metrics: {
    totalExceptions: number;
    pendingExceptions: number;
    acceptedExceptions: number;
    deniedExceptions: number;
  };
}

export interface OMExceptionMetrics {
  totalExceptions: number;
  pendingExceptions: number;
  acceptedExceptions: number;
  deniedExceptions: number;
}
